import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import ProfilePicture from "../../profileImage";

const MemberCard = ({ member, teamId }) => {
  const { t } = useTranslation();
  const url = member.type === 'player' ? `/organization/player/${member.userId}` : `/organization/team/${teamId}/member/${member.userId}`;
  return (
    <Link to={url} key={member.userId} className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow">
      <div className="p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-shrink-0">
            <ProfilePicture
              image={member.profilePicture}
              alt={t("navigation.profile")}
              className="h-12 w-12 rounded-full object-cover"
              myUser={member}
              user={member}
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">
              {member.userName}
            </p>
            <p className="text-sm text-gray-500">
              {member.type?.charAt(0).toUpperCase() + member.type?.slice(1)}
            </p>
            <p className="text-xs text-gray-400">
              Joined {new Date(member.joinedAt).toLocaleDateString()}
            </p>
          </div>
          <div className="flex-shrink-0">
            {member.role?.name && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {member.role.name}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  )
};

const StatCard = ({ title, value, color = "text-[#F15213]" }) => (
  <div className="bg-white p-6 rounded-xl shadow-sm">
    <h3 className="text-lg font-medium text-gray-900">{title}</h3>
    <p className={`mt-2 text-3xl font-bold ${color}`}>
      {value}
    </p>
  </div>
);

const MemberSection = ({ title, members, count, badgeColor, teamId }) => (
  <div>
    <div className="flex items-center justify-between mb-4">
      <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
      <span className={`px-3 py-1 ${badgeColor} rounded-full text-sm font-medium`}>
        {count} {title}
      </span>
    </div>
    {members.length > 0 ? (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {members.map(member => (
            <MemberCard 
              key={member.userId}
              member={member} 
            teamId={teamId}
            />
        ))}
      </div>
    ) : (
      <div className="text-center py-8 bg-gray-50 rounded-lg">
        <p className="text-gray-500">No {title.toLowerCase()} found</p>
      </div>
    )}
  </div>
);

const TeamOverviewTab = ({ team, teamMembers }) => {
  const { t } = useTranslation();
  
  if (!team) {
    return (
      <div className="mt-8 text-center">
        <p className="text-gray-500">Loading team information...</p>
      </div>
    );
  }

  const getPlayerCount = () => {
    return teamMembers.filter(member => member.type === 'player').length;
  };

  const getStaffCount = () => {
    return teamMembers.filter(member => member.type === 'supervisor').length;
  };

  const playerMembers = teamMembers.filter(member => member.type === 'player');
  const staffMembers = teamMembers.filter(member => member.type === 'supervisor');

  return (
    <div role="tabpanel" id="overview-panel" aria-labelledby="overview-tab">
      {/* Team Statistics */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard title={t("navigation.players")} value={getPlayerCount()} />
        <StatCard title="Staff" value={getStaffCount()} />
        <StatCard 
          title="Created" 
          value={new Date(team.createdAt).toLocaleDateString()} 
          color="text-gray-600"
        />
      </div>

      {/* Team Members Sections */}
      {teamMembers.length > 0 ? (
        <div className="mt-8 space-y-8">
          {/* Players Section */}
          <MemberSection 
            title={t("navigation.players")}
            members={playerMembers}
            count={getPlayerCount()}
            badgeColor="bg-blue-100 text-blue-800"
            teamId={team._id}
          />

          {/* Supervisors Section */}
          <MemberSection 
            title="Supervisors"
            members={staffMembers}
            count={getStaffCount()}
            badgeColor="bg-green-100 text-green-800"
            teamId={team._id}
          />
        </div>
      ) : (
        <div className="mt-8 p-8 bg-gray-50 rounded-lg text-center">
          <p className="text-gray-500">No team members yet</p>
          <button className="mt-4 px-4 py-2 bg-[#F15213] text-white rounded-lg hover:bg-[#d14711] transition-colors">
            Add Team Members
          </button>
        </div>
      )}
    </div>
  );
};

export default TeamOverviewTab; 